import { useOktaAuth } from '@okta/okta-react';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { CILogger } from 'common/log/log.provider';
import type { ComponentWithoutHOCProps } from 'common/types/component';
import { Loading } from 'common/utils/loading';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';

import { withAuthLayout } from '../login/authLayout.page';

import { OktaErrorDisplay } from './OktaErrorDisplay';

// Inspired by https://github.com/okta/okta-react/blob/master/src/LoginCallback.tsx
let handledRedirect = false;

const _OktaLoginCallback = (): ReactElement => {
    const { t } = useTranslation('auth');
    const { oktaAuth, authState } = useOktaAuth();
    const location = useLocation();
    const [callbackError, setCallbackError] = useState(null);

    useEffect(() => {
        // OKTA-635977: Prevents multiple calls of handleLoginRedirect() in React18 StrictMode
        // Top-level variable solution follows: https://react.dev/learn/you-might-not-need-an-effect#initializing-the-application
        if (!handledRedirect) {
            const searchParams = new URLSearchParams(location.search);
            const hasState = searchParams.has('state');

            if (!hasState || authState?.isAuthenticated === true) {
                // Not a valid callback or user already authenticated (browser back to callback URL).
                window.location.href = '/';

                return;
            }

            oktaAuth.handleRedirect('/sso/okta/postLogin').catch(async (e) => {
                const isStalePkceCallback = Boolean(
                    (e as { message?: string })?.message?.includes?.('Could not load PKCE codeVerifier from storage')
                );
                const isOktaAuthenticated = !isStalePkceCallback && (await oktaAuth.isAuthenticated());

                if (isStalePkceCallback) {
                    CILogger.getInstance().info('Recovered from stale Okta callback URL', e);
                } else if (isOktaAuthenticated) {
                    CILogger.getInstance().info('Okta handleRedirect error ignored — user is already authenticated', e);
                } else {
                    CILogger.getInstance().error('Unrecoverable Okta callback failure', e);
                    CIPageStatus.error(t('login.sso.error.gather_error_description'));
                    setCallbackError(e);

                    return;
                }

                try {
                    await CILogger.getInstance().flush();
                } catch (flushError) {
                    CILogger.getInstance().info(
                        'Failed to flush logs before redirecting from Okta callback',
                        flushError
                    );
                } finally {
                    window.location.href = '/';
                }
            });
            handledRedirect = true;
        }
    }, [oktaAuth, location.search, authState?.isAuthenticated, t]);

    const authError = authState?.error;
    const displayError = callbackError || authError;

    const handleGoLoginPage = async () => {
        await oktaAuth?.signOut();
        window.location.href = '/';
    };

    if (displayError) {
        return <OktaErrorDisplay error={displayError} onGoToLogin={handleGoLoginPage} />;
    }

    return <Loading isLoading={true} />;
};

const OktaLoginCallback: ComponentWithoutHOCProps<null> = withAuthLayout(_OktaLoginCallback);

export default OktaLoginCallback;
